import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SVG_SIZE = 400;
const LABEL_OFFSET = 16;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function PolarAreaChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Sort by volatility descending so highest is at 12 o'clock
    const sorted = [...data].sort((a, b) => b.volatility - a.volatility);
    const n = sorted.length;

    const cx = SVG_SIZE / 2;
    const cy = SVG_SIZE / 2;
    const maxRadius = (SVG_SIZE / 2) * 0.82;

    svg
      .attr('viewBox', `0 0 ${SVG_SIZE} ${SVG_SIZE}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .style('max-width', `${SVG_SIZE}px`);

    const g = svg.append('g').attr('transform', `translate(${cx}, ${cy})`);

    // Volatility scale: maps volatility to radius
    const maxVolatility = d3.max(sorted, (d) => d.volatility);
    const radiusScale = d3
      .scaleLinear()
      .domain([0, maxVolatility])
      .range([0, maxRadius]);

    // Reference circles at 25%, 50%, 75%, 100% of max volatility
    const refLevels = [0.25, 0.5, 0.75, 1.0];
    refLevels.forEach((level) => {
      const refVal = maxVolatility * level;
      const r = radiusScale(refVal);

      g.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', r)
        .attr('fill', 'none')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-width', 0.8);

      // Label at top of reference circle
      g.append('text')
        .attr('x', 0)
        .attr('y', -r)
        .attr('dy', '-3')
        .attr('text-anchor', 'middle')
        .attr('font-size', '8px')
        .attr('fill', '#c0c4cc')
        .text(`${(refVal).toFixed(1)}%`);
    });

    // Angle for each state wedge (equal angles)
    const anglePerState = (2 * Math.PI) / n;
    const padAngle = 0.02;

    // Arc generator
    const arcGen = d3.arc();

    sorted.forEach((d, i) => {
      const startAngle = i * anglePerState - Math.PI / 2 + padAngle / 2;
      const endAngle = (i + 1) * anglePerState - Math.PI / 2 - padAngle / 2;
      const outerR = radiusScale(d.volatility);

      // Main wedge
      g.append('path')
        .attr(
          'd',
          arcGen({
            innerRadius: 0,
            outerRadius: outerR,
            startAngle: startAngle + Math.PI / 2,
            endAngle: endAngle + Math.PI / 2,
          })
        )
        .attr('fill', d.color)
        .attr('fill-opacity', 0.75)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1)
        .style('cursor', 'pointer')
        .on('mouseenter', function (event) {
          d3.select(this)
            .transition()
            .duration(150)
            .attr('fill-opacity', 1)
            .attr(
              'd',
              arcGen({
                innerRadius: 0,
                outerRadius: outerR + 5,
                startAngle: startAngle + Math.PI / 2,
                endAngle: endAngle + Math.PI / 2,
              })
            );
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 14,
            state: d.state,
            volatility: d.volatility,
            avgPrice: d.avgPrice,
            addressCount: d.addressCount,
          });
        })
        .on('mousemove', function (event) {
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 14,
            state: d.state,
            volatility: d.volatility,
            avgPrice: d.avgPrice,
            addressCount: d.addressCount,
          });
        })
        .on('mouseleave', function () {
          d3.select(this)
            .transition()
            .duration(150)
            .attr('fill-opacity', 0.75)
            .attr(
              'd',
              arcGen({
                innerRadius: 0,
                outerRadius: outerR,
                startAngle: startAngle + Math.PI / 2,
                endAngle: endAngle + Math.PI / 2,
              })
            );
          setTooltip(null);
        });

      // State label at outer edge of wedge
      const midAngle = (startAngle + endAngle) / 2;
      const labelR = Math.max(outerR, maxRadius * 0.3) + LABEL_OFFSET;
      const lx = labelR * Math.cos(midAngle);
      const ly = labelR * Math.sin(midAngle);

      // Determine text anchor based on angle position
      const angleDeg = (midAngle * 180) / Math.PI;
      let textAnchor = 'middle';
      if (angleDeg > 20 && angleDeg < 160) textAnchor = 'start';
      else if (angleDeg > -160 && angleDeg < -20) textAnchor = 'end';

      g.append('text')
        .attr('x', lx)
        .attr('y', ly)
        .attr('text-anchor', textAnchor)
        .attr('dominant-baseline', 'central')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', '#4b5563')
        .text(d.state);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Volatility Rose</div>
        <div style={styles.subtitle}>
          Wedge size shows coefficient of variation in pest365 pricing per state
        </div>
        <div style={styles.emptyState}>No volatility data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Volatility Rose</div>
      <div style={styles.subtitle}>
        Wedge size shows coefficient of variation in pest365 pricing per state
      </div>
      <div style={styles.chartContainer}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '6px',
              fontSize: '13px',
            }}
          >
            {tooltip.state}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Volatility</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.volatility.toFixed(2)}%
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ${tooltip.avgPrice.toFixed(2)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.addressCount}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
